/**
 * What each row of the order tables shows and offers (IP-125..IP-133; design
 * 2026-10-06-order-review-business-day-design.md 8.2 to 8.5), kept free of
 * React so node --test covers it (orderRows.test.js). The tables, the review
 * dialog and the order drawer ask these and render the answers; none of them
 * decides anything of its own.
 *
 *   the buttons       Review (any reviewable state, unless the business day
 *                     pauses reviews for the order's date - IP-129), Reject
 *                     (an open state once LIV has completed, a checker's -
 *                     IP-130, A-2) and Approve
 *                     (READY, reviewed in its cycle, and never by one of its
 *                     reviewers - IP-128, A-3), each with the sentence that
 *                     says why it is off. On Pending transactions only a fund
 *                     transfer's row carries Approve (approvesFromPending);
 *                     every other order is approved from Ready for approval
 *   Review by         the cut-off an unreviewed order must be reviewed by,
 *                     shown only where a review decides whether it freezes
 *                     into that day or rolls to the next (IP-125, IP-126), and
 *                     how often it rolled already
 *   Reviews           how many reviews its current cycle has, and who
 *                     reviewed it (IP-127)
 *   the price         the provisional unit price its figures stand at and its
 *                     basis (B-3, IP-132, design 4.5), and the side of the
 *                     order that price moves
 *   the history       review, repricing and state history in words, each
 *                     person named
 *
 * The server decides all of it again: the page only declines to offer what
 * the server would refuse, in the server's words where it has them.
 */
import { REJECTABLE_STATES, REVIEWABLE_STATES } from '@lfoc/domain/approval/gates.js';
import { cmp, isDecimal } from '@lfoc/domain/decimal.js';

import { formatMoney, formatUnits } from '../../../format.js';
import { HOLD_REASON_LABEL, OPEN_STATES, STATE_LABEL } from './orders.js';

const COLOMBO = 'Asia/Colombo';
const MINUTE = 60 * 1000;

/** A deadline turns red this close to its cut-off (design 8.2). */
export const REVIEW_BY_URGENT_MS = 30 * MINUTE;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * IP-176: a subscription being paid in parts - its money still EXPECTED,
 * with some of it in (GET orders/{id} money.receivedAmount, what its parts
 * have paid so far) - in the drawer's words: 'Received LKR 40,000.00 of
 * 100,000.00'. Null otherwise, and the Money row keeps its usual figure.
 * IP-184: a redemption's payout paid in parts (one debit per payout
 * account) says 'Paid LKR ... of ...' instead.
 */
export function receivedText(money, { payout = false } = {}) {
    if (money?.state !== 'EXPECTED' || !isDecimal(money.receivedAmount) || cmp(money.receivedAmount, '0') <= 0) return null;
    return (payout ? 'Paid LKR ' : 'Received LKR ') + formatMoney(money.receivedAmount) + ' of ' + formatMoney(money.amount);
}

/**
 * IP-184: a redemption's payout (its RED_PAYOUT movement) in words - unpaid
 * until a statement debit (or a payout file's item) is matched to it, paid
 * once confirmed. Any other state as it is.
 */
export const PAYOUT_STATE_LABEL = Object.freeze({ EXPECTED: 'Unpaid', CONFIRMED: 'Paid (reconciled)' });

/** '6 Oct 2026' for 2026-10-06 - the review pause's own words (business-day/pause.js pauseRefusal). */
export const dateLabel = (date) => Number(date.slice(8, 10)) + ' ' + MONTHS[Number(date.slice(5, 7)) - 1] + ' ' + date.slice(0, 4);

const msOf = (iso) => (iso ? Date.parse(iso) : NaN);
const colomboDay = (ms) => new Date(ms).toLocaleDateString('en-CA', { timeZone: COLOMBO });
const colomboClock = (ms) => new Date(ms).toLocaleTimeString('en-GB', { timeZone: COLOMBO, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });

/**
 * An instant as the Colombo clock reads it, short: '14:00' on the day of
 * `nowMs`, '7 Oct, 14:00' on any other.
 */
export function colomboTime(iso, nowMs = Date.now()) {
    const ms = msOf(iso);
    if (!Number.isFinite(ms)) return '—';
    const day = colomboDay(ms);
    return (day === colomboDay(nowMs) ? '' : Number(day.slice(8, 10)) + ' ' + MONTHS[Number(day.slice(5, 7)) - 1] + ', ') + colomboClock(ms);
}

const ref = (order) => order?.transactionRef ?? order?.orderId;
const count = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0);

/** The current cycle's review count; a read from before 0018 carries only reviewedBy. */
const reviewCountOf = (order) => (order?.reviewCount === undefined || order?.reviewCount === null
    ? (order?.reviewedBy ? 1 : 0)
    : count(order.reviewCount));

/** The pause's words for each step (approval/review.js STEP_LABEL). */
const STEP_LABEL = Object.freeze({ REPRICING: 'Transaction pricing', DAY_END: 'Day end' });

/**
 * IP-129 (design 2.6): whether the business day pauses this order's review -
 * a pause is on, and the order is dated on or after the paused day. Orders
 * dated before it never are: they are what that day waits for. An order
 * with no dealing date pauses nothing (it cannot be reviewed anyway).
 *
 * @param {object} order a GET /orders row
 * @param {{ paused: boolean, businessDate: string, stage: string, stageLabel?: string }|null} reviewPaused GET /books/days's
 */
export function pausedFor(order, reviewPaused) {
    if (!reviewPaused?.paused || typeof reviewPaused.businessDate !== 'string') return false;
    return typeof order?.dealingDate === 'string' && order.dealingDate >= reviewPaused.businessDate.slice(0, 10);
}

/** The pause in the server's own sentence (business-day/pause.js pauseRefusal), for the greyed button's tooltip. */
export function pauseSentence(reviewPaused) {
    if (!reviewPaused?.paused || typeof reviewPaused.businessDate !== 'string') return null;
    const step = reviewPaused.stageLabel ?? STEP_LABEL[reviewPaused.stage] ?? reviewPaused.stage;
    return 'Reviews are paused while the business day of ' + dateLabel(reviewPaused.businessDate.slice(0, 10)) + ' is ' + step
        + '. They reopen when it finishes.';
}

/** Why an order in this state cannot be reviewed (design 2.1), in words the person can act on. */
function notReviewable(order) {
    // IP-177: never listed - the server leaves it out - so this is only a guard.
    if (PARKED.has(order.state)) return 'The client has not finished this transaction in LIV yet';
    // IP-200: a held transaction is only ever rejected in the Console.
    if (order.state === 'HELD') return 'A transaction on hold cannot be reviewed; reject it if it should not go ahead';
    return 'Transaction ' + ref(order) + ' is ' + (STATE_LABEL[order.state] ?? order.state) + '; it cannot be reviewed in that state';
}

/**
 * The Review button of a row (design 8.2): on for an order in a reviewable
 * state (approval/gates.js REVIEWABLE_STATES - from LIV's completion to
 * READY; IP-127), unless reviews are paused for its date (IP-129). It reads
 * "Review Again" for a reader who reviewed the order before, in any cycle:
 * several people may review an order, the same person again.
 *
 * @returns {{ enabled: boolean, label: string, reason: string|null }}
 */
export function reviewAction(order, { canReview, reviewPaused = null } = {}) {
    const label = order?.reviewedByMe === true ? 'Review Again' : 'Review';
    if (!canReview) return { enabled: false, label, reason: 'Your roles cannot review transactions' };
    if (!REVIEWABLE_STATES.includes(order.state)) return { enabled: false, label, reason: notReviewable(order) };
    if (pausedFor(order, reviewPaused)) return { enabled: false, label, reason: pauseSentence(reviewPaused) };
    return { enabled: true, label, reason: null };
}

/**
 * The Reject button of a row (IP-130, A-2): a checker's, from any open state
 * (approval/gates.js REJECTABLE_STATES). HELD stays rejectable. A pause of
 * reviews does not grey Reject. IP-177: an order the client has not finished
 * in LIV is never on the screen, and the server refuses it
 * (LIV_WORKFLOW_INCOMPLETE); Reject is off for one all the same.
 *
 * @returns {{ enabled: boolean, reason: string|null }}
 */
export function rejectAction(order, { canReject } = {}) {
    if (!canReject) return { enabled: false, reason: 'A checker rejects transactions' };
    if (PARKED.has(order.state)) return { enabled: false, reason: 'The client has not finished this transaction in LIV yet' };
    if (!REJECTABLE_STATES.includes(order.state)) {
        return { enabled: false, reason: 'Transaction ' + ref(order) + ' is ' + (STATE_LABEL[order.state] ?? order.state) + '; it is no longer open' };
    }
    return { enabled: true, reason: null };
}

/**
 * The Approve button of a row (G-1, G-6; IP-128, A-3): a READY order,
 * reviewed in its current cycle, by a checker who reviewed it in no cycle -
 * the words are G-6's own - on a dealing date the pricing guard does not
 * hold: blockedFunds are the funds GET /pricing/guard says have no published
 * price for it (orders.js blockedDates), and the server refuses the whole
 * request for them (PRICING_GUARD), as the Approve queue already knows.
 *
 * @param {object} order
 * @param {{ canApprove?: boolean, blockedFunds?: string[]|null }} [options]
 * @returns {{ enabled: boolean, reason: string|null }}
 */
export function approveAction(order, { canApprove, blockedFunds = null } = {}) {
    if (!canApprove) return { enabled: false, reason: 'Your roles cannot approve transactions' };
    if (order.state !== 'READY') return { enabled: false, reason: 'Approve is available when the transaction is Ready for Approval' };
    if (reviewCountOf(order) === 0) return { enabled: false, reason: 'Transaction ' + ref(order) + ' has not been reviewed.' };
    if (order.reviewedByMe === true) return { enabled: false, reason: 'You reviewed transaction ' + ref(order) + '; someone who has not reviewed it must approve it.' };
    if (blockedFunds?.length) {
        return { enabled: false, reason: 'No published price for ' + blockedFunds.join(', ') + ' on ' + order.dealingDate + ': approval is held' };
    }
    // IP-193 (approval/gates.js APR-015): a redemption or switch-out is paid by the bank first and
    // approved after - Approve waits for the bank statement to show it paid in full.
    if (paidBeforeApproval(order) && order.payoutState !== 'CONFIRMED') {
        return { enabled: false, reason: 'Payout not reconciled: the bank statement has not shown transaction ' + ref(order) + ' paid. Reconcile it before approving.' };
    }
    return { enabled: true, reason: null };
}

/** IP-193: a redemption or switch-out with a net to pay - reconciled with the bank statement before approval. */
const paidBeforeApproval = (order) => (order.orderType === 'RED' || order.orderType === 'SWI_OUT')
    && !(order.netAmount !== null && order.netAmount !== undefined && Number(order.netAmount) === 0);

/**
 * IP-125: whether a review is what decides this order's next step at its
 * book's cut-off - it freezes into the day if reviewed in time, and rolls to
 * the next dealing day if not. A validated order that is not a subscription,
 * and a subscription whose money has cleared: anything else waits for LIV or
 * for money (and an unfunded subscription rolls whatever its review), or is
 * past the freeze already.
 */
export const freezesIfReviewed = (order) => (order.state === 'VALIDATED' && order.orderType !== 'SUB')
    || (order.state === 'MATCHED_CLEARED' && order.orderType === 'SUB');

/**
 * The Review by column (design 8.2; IP-125, IP-126): on an unreviewed order
 * that would freeze if reviewed, in an OPEN book - "Review by 14:00", red
 * within 30 minutes of the cut-off; "Cut-off passed" once it has (the book
 * is not closed yet, but the order will roll when it is: a review now counts
 * for the next day). And "Rolled 2×" on any order that rolled before for
 * want of a review.
 *
 * @param {object} order a GET /orders row
 * @param {number} nowMs
 * @returns {{ deadline: { text: string, urgent: boolean, passed: boolean, tooltip: string } | null, rolled: number, rolledText: string|null }}
 */
export function reviewBy(order, nowMs = Date.now()) {
    const rolled = count(order.rollCount);
    const out = {
        deadline: null,
        rolled,
        rolledText: rolled ? 'Rolled ' + rolled + '×' : null
    };
    const cutoff = msOf(order.book?.cutoffAt);
    if (reviewCountOf(order) > 0 || !freezesIfReviewed(order) || order.book?.state !== 'OPEN' || !Number.isFinite(cutoff)) return out;
    const time = colomboTime(order.book.cutoffAt, nowMs);
    const left = cutoff - nowMs;
    // IP-191 (owner, 17 Oct 2026): a review before the cut-off is welcome but no longer required -
    // the transaction is dealt on its own date either way, and reviewed again after pricing. So the
    // column informs; it never warns of a roll, and nothing is urgent.
    out.deadline = left > 0
        ? { text: 'Review by ' + time, urgent: false, passed: false, tooltip: 'Review before the cut-off (' + time + '), or after it once the transaction is priced. It is dealt on its date either way.' }
        : {
            text: 'Review after pricing',
            urgent: false,
            passed: true,
            tooltip: 'The cut-off (' + time + ') has passed: the transaction is dealt on its date and priced with that date\'s price. Review it then.'
        };
    return out;
}

/** The roll count in words, for a tooltip. */
export const rolledTooltip = (rolled) => 'Rolled to a later dealing day ' + (rolled === 1 ? 'once' : rolled + ' times') + ', not reviewed by the cut-off';

/**
 * IP-197 (owner, 17 Oct 2026): "we can accept the review before pricing, but need to flag and show the price has
 * changed after the previous review". The order read's repricedSinceReview (approval/review.js): its figures were
 * priced again after its latest review. Approval stays open; the person approving is shown which ones.
 */
export const PRICE_CHANGED_TEXT = 'Price changed since review';

/** The transactions among these priced again since their latest review, by reference (the Approve confirmation's list). */
export const repricedSinceReviewRefs = (orders) => (orders ?? []).filter((o) => o?.repricedSinceReview === true).map((o) => ref(o));

/** The reviewers' names (each once, in the order they first reviewed), a uid where the read named nobody. */
export const reviewerNames = (order) => (order?.reviewers ?? []).map((r) => r.name ?? r.uid).filter(Boolean);

/**
 * The Reviews column (design 8.2): the current cycle's count, "incl. you"
 * when the reader is one of the order's reviewers, and a tooltip naming
 * them. An order sent back has a count of 0 and its earlier reviewers named.
 *
 * @returns {{ count: number, text: string, tooltip: string }}
 */
export function reviewsSummary(order) {
    const n = reviewCountOf(order);
    const names = reviewerNames(order);
    const you = order?.reviewedByMe === true;
    return {
        count: n,
        text: n ? n + (you ? ' incl. you' : '') : 'None',
        tooltip: !names.length
            ? 'Not reviewed yet'
            : n ? 'Reviewed by ' + names.join(', ') : 'Sent back for a new review; reviewed before by ' + names.join(', ')
    };
}

/** The tag a price basis carries (design 8.2) and what it means. */
export const PRICE_TAG = Object.freeze({
    LATEST: { label: 'prov.', tooltip: 'Provisional: the fund\'s latest published price. Priced again when the dealing date\'s own price is published.' },
    DEALING_DATE: { label: 'dealing-date price', tooltip: 'The dealing date\'s own published price: what the transaction is allocated at.' },
    ESTIMATE: { label: 'estimate', tooltip: 'Intake\'s estimate of the units, reserved against the holding until LIV completes the instruction.' }
});

const TRANSFERS = new Set(['TRF_OUT', 'TRF_IN']);

/**
 * Whether the Pending transactions row of this order carries Approve: only a
 * fund transfer's (TRF_OUT, TRF_IN). Every other order is approved from Ready
 * for approval, which keeps Approve for all of them - the pending list is
 * where orders are reviewed and rejected.
 */
export const approvesFromPending = (order) => TRANSFERS.has(order?.orderType);
const PARKED = new Set(['RECEIVED', 'AWAITING_LIV']);
/** The figures of an order still open are provisional; those of an ended one are only a record. */
const STILL_OPEN = new Set(['RECEIVED', ...OPEN_STATES]);

/**
 * The Unit price column (B-3; design 4.5's exceptions): the price the
 * order's figures stand at - provisional (LATEST, "prov."), the dealing
 * date's own (DEALING_DATE), or the allocated price - and, where there is
 * none, why: a parked order's units are intake's estimate; a transfer deals
 * at cost; a held order with no date waits on its hold; anything else has not
 * been repriced yet.
 *
 * @returns {{ price: string|null, tag: { label: string, tooltip: string } | null, text: string|null, note: string|null, tooltip: string|null }}
 */
export function pricingDisplay(order) {
    const pricing = order?.pricing ?? null;
    const basis = pricing?.basis ?? null;
    const none = { price: null, tag: null, text: null, note: null, tooltip: null };
    if (basis === 'ALLOCATED' && pricing.price !== null && pricing.price !== undefined) {
        return { ...none, price: pricing.price, tooltip: 'The price the transaction was allocated at' };
    }
    if ((basis === 'LATEST' || basis === 'DEALING_DATE') && pricing.price !== null && pricing.price !== undefined) {
        return { ...none, price: pricing.price, tag: PRICE_TAG[basis], tooltip: PRICE_TAG[basis].tooltip };
    }
    if (basis === 'ESTIMATE') return { ...none, text: 'Priced when LIV completes', tag: PRICE_TAG.ESTIMATE, tooltip: PRICE_TAG.ESTIMATE.tooltip };
    if (TRANSFERS.has(order?.orderType)) return { ...none, text: 'Dealt at cost', tooltip: 'A transfer moves units at their cost: no unit price applies' };
    if (PARKED.has(order?.state)) return { ...none, text: 'Priced when LIV completes' };
    if (order?.state === 'HELD' && !order.dealingDate) {
        return { ...none, text: '—', note: HOLD_REASON_LABEL[order.holdReasonCode] ?? order.holdReasonCode ?? null, tooltip: 'Not priced while on hold' };
    }
    return { ...none, text: '—', tooltip: 'Not priced yet: a provisional price is set once a published price applies' };
}

/**
 * The sides of an order its price moves (design 8.2: "the variable side
 * carries the same tag"): repricing keeps the side the investor fixed and
 * works out the other (repricing/quote.js FIXED_SIDE) - units for an amount,
 * the amount for units or a full holding, both for a switch-in derived from
 * its switch-out.
 *
 * @returns {Array<'units'|'amount'>}
 */
export function variableSides(order) {
    const pricing = order?.pricing;
    if (!pricing || !['LATEST', 'DEALING_DATE'].includes(pricing.basis)) return [];
    switch (pricing.fixedSide) {
        case 'AMOUNT': return ['units'];
        case 'UNITS':
        case 'FULL_HOLDING': return ['amount'];
        case 'DERIVED': return ['units', 'amount'];
        default: return [];
    }
}

/** Whether the Core has not repriced the transaction yet: no price of its own, so its variable side is LIV's (T-1). */
export function awaitingRepricing(order) {
    return !['LATEST', 'DEALING_DATE', 'ALLOCATED'].includes(order?.pricing?.basis);
}

/**
 * The sides LIV worked out rather than the investor fixed, which stay hidden
 * until the Core reprices (figureOf): an LKR based order's units, a unit based
 * one's LKR (a full holding's LKR too), and both for a switch-in, whose LKR
 * comes from its switch-out. A transfer moves at cost: nothing is priced.
 *
 * @returns {Array<'units'|'amount'>}
 */
export function hiddenUntilRepriced(order) {
    if (TRANSFERS.has(order?.orderType)) return [];
    if (order?.basis === 'DERIVED') return ['units', 'amount'];
    if (order?.isFullHolding || order?.basis === 'UNITS') return ['amount'];
    if (order?.basis === 'AMOUNT') return ['units'];
    return [];
}

/**
 * The figure a Units or Amount cell shows, and the tag it carries: the
 * order's own figure, tagged with its price basis when it is the side that
 * price moves and the order is still open; a parked order's units, when it
 * has none yet, are intake's reserved estimate.
 *
 * @param {object} order
 * @param {'units'|'amount'} side
 * @returns {{ value: string|null, tag: { label: string, tooltip: string } | null }}
 */
export function figureOf(order, side) {
    // ------------------------------------------------------------------------
    // HIDE LIV'S T-1 FIGURE UNTIL THE CORE REPRICES (requested 15 Oct 2026,
    // pending the client's confirmation - kept in this one block so it can be
    // removed or commented out in one place).
    //
    // LIV works out the side the investor did NOT fix at the previous day's
    // (T-1) price: an LKR based subscription's units, a unit based
    // redemption's LKR. That figure is misleading until the Core reprices
    // the transaction, so until then only the side the investor fixed is
    // shown. Once the Core has repriced it (at any price: provisional, its
    // dealing date's, or allocated) every figure shows.
    if (awaitingRepricing(order) && hiddenUntilRepriced(order).includes(side)) {
        return { value: null, tag: null, awaitingRepricing: true };
    }
    // ------------------------------------------------------------------------
    const own = order?.[side] ?? null;
    if (side === 'units' && own === null && order?.pricing?.basis === 'ESTIMATE' && order.reservedUnits !== null && order.reservedUnits !== undefined) {
        return { value: order.reservedUnits, tag: PRICE_TAG.ESTIMATE };
    }
    const tagged = own !== null && STILL_OPEN.has(order?.state) && variableSides(order).includes(side);
    return { value: own, tag: tagged ? PRICE_TAG[order.pricing.basis] : null };
}

/** The price side's words (register/allocate.js PRICE_SIDE). */
export const PRICE_SIDE_LABEL = Object.freeze({
    SELLING: 'Selling price',
    // IP-203: a subscription or switch-in dealt with its front-end fee.
    SELLING_WITH_ENTRY_FEE: 'Selling price with entry fee',
    BUYING: 'Buying price',
    BUYING_WITH_EXIT_FEE: 'Buying price with exit fee',
    MIXED: 'Buying price, with the exit fee on some lots'
});

/** The repricing reasons (IP-132, design 4.2), as the history reads them. */
export const REPRICE_REASON_LABEL = Object.freeze({
    ORDER_ACCEPTED: 'Accepted from LIV',
    PRICE_PUBLISHED: 'A price was published',
    DAY_REPRICING: 'The business day\'s pricing',
    // IP-139: a review set the order's own entry-fee rate (before IP-203; kept for the history).
    FEE_OVERRIDE: 'The entry-fee rate was set at review',
    // IP-203: a review ticked or unticked Apply fees.
    FEE_TOGGLE: 'Apply fees was changed at review'
});

/**
 * Who did something, as a person reads it: their name where the read named
 * them (IP-112), else the uid of a CONSOLE:<uid> actor; the system's own steps
 * by name.
 */
export function actorText(actor, name = null) {
    if (name) return name;
    if (typeof actor !== 'string' || !actor) return '—';
    if (actor.startsWith('CONSOLE:')) return actor.slice('CONSOLE:'.length);
    if (actor.startsWith('SYSTEM:')) return 'System (' + actor.slice('SYSTEM:'.length) + ')';
    return actor;
}

/** order_state_history events in words; anything else shows its own code. */
export const HISTORY_EVENT_LABEL = Object.freeze({
    ORDER_RECEIVED: 'Received from LIV',
    AWAITING_LIV: 'Waiting for LIV',
    FUNDS_EXPECTED: 'Funds expected',
    INTAKE_REJECTED: 'Rejected at intake',
    INTAKE_HELD: 'Held at intake',
    T9: 'Frozen into the book',
    BOOK_CLOSED_PROVISIONAL_ROLL: 'Rolled: not funded by the cut-off',
    BOOK_CLOSED_REVIEW_ROLL: 'Rolled: not reviewed by the cut-off',
    REPRICE: 'Priced',
    CORE_REJECTED: 'Rejected by a checker',
    ALLOCATION_SHORTFALL: 'Allocation shortfall'
});

/**
 * One order_state_history row in words (design 8.4): what happened, the move
 * (a state change, or a roll's dates), its reason code, and who - named.
 *
 * @returns {{ title: string, move: string|null, reason: string|null, actor: string, roll: boolean }}
 */
export function historyEntry(h) {
    const roll = h.event === 'BOOK_CLOSED_REVIEW_ROLL' || h.event === 'BOOK_CLOSED_PROVISIONAL_ROLL';
    const dates = h.details && typeof h.details === 'object' && h.details.fromDealingDate && h.details.toDealingDate
        ? h.details.fromDealingDate + ' → ' + h.details.toDealingDate
        : null;
    const states = h.fromState && h.fromState !== h.toState
        ? (STATE_LABEL[h.fromState] ?? h.fromState) + ' → ' + (STATE_LABEL[h.toState] ?? h.toState)
        : STATE_LABEL[h.toState] ?? h.toState ?? null;
    return {
        title: HISTORY_EVENT_LABEL[h.event] ?? h.event,
        move: roll && dates ? 'Effective date ' + dates : states,
        reason: h.reasonCode ?? null,
        actor: actorText(h.actor, h.actorName ?? null),
        roll
    };
}

/**
 * One order_review row in words (design 8.4): the reviewer by name, the kind,
 * and what they wrote - a review's comment, a send-back's reason.
 *
 * @returns {{ kind: string, who: string, text: string|null, sentBack: boolean }}
 */
export function reviewEntry(r) {
    const sentBack = r.kind === 'SENT_BACK';
    return {
        kind: sentBack ? 'Sent back' : 'Reviewed',
        who: r.actorName ?? r.actorUid ?? '—',
        text: (sentBack ? r.reason : r.comment ?? r.reason) ?? null,
        sentBack
    };
}

/**
 * IP-187 (owner, 16 Oct 2026): what a transaction's history shows. "Transaction history should
 * only include: received from LIV, client completed the transaction, reviews and accept or
 * reject, pricing, reconcile, approve, units created / released." Every other move the register
 * records - frozen into the book, priced and queued by the gate (T10, T11), rolled, held,
 * funds expected, posted to LIV - stays in order_state_history for audit, but is not shown.
 *
 * The seven kinds of entry, and where each comes from in GET /orders/{orderId}:
 *
 *   RECEIVED   history ORDER_RECEIVED                         "Received from LIV"
 *   COMPLETED  history INTAKE_PASSED or LIV_COMPLETED          "Client completed the transaction"
 *   REVIEW     reviews REVIEWED / SENT_BACK                   "Reviewed by …" / "Sent back by …"
 *   REJECTED   history INTAKE_REJECTED, CORE_REJECTED,        "Rejected"
 *              ALLOCATION_SHORTFALL, HOLD_REJECTED
 *   PRICING    repricings (one per pricing run that moved a   "Priced at …"
 *              figure, with its before -> after)
 *   RECONCILE  history T5 (money matched and cleared),        "Reconciled …"
 *              T6 (matched, cheque in clearing), T7 (cheque
 *              cleared), PART_RECEIVED (part of the money)
 *   APPROVE    history T12 (READY -> APPROVED)                "Approved"
 *   UNITS      history T13 (APPROVED -> ALLOCATED)            "Units created" (subscription, switch-in,
 *                                                             transfer-in) / "Units released" (the rest)
 */
export const TIMELINE_STEP = Object.freeze({
    RECEIVED: 'RECEIVED', COMPLETED: 'COMPLETED', REVIEW: 'REVIEW', REJECTED: 'REJECTED',
    PRICING: 'PRICING', RECONCILE: 'RECONCILE', APPROVE: 'APPROVE', UNITS: 'UNITS',
    // IP-193: a redemption's payout the bank has (part) paid no longer matches the transaction - a person must look.
    PAYOUT_ALERT: 'PAYOUT_ALERT'
});

/** State-history events shown, by the step each belongs to. Anything not here is left out. */
const SHOWN_HISTORY = Object.freeze({
    ORDER_RECEIVED: { step: TIMELINE_STEP.RECEIVED, title: 'Received from LIV' },
    INTAKE_PASSED: { step: TIMELINE_STEP.COMPLETED, title: 'Client completed the transaction' },
    LIV_COMPLETED: { step: TIMELINE_STEP.COMPLETED, title: 'Client completed the transaction' },
    INTAKE_REJECTED: { step: TIMELINE_STEP.REJECTED, title: 'Rejected at intake' },
    CORE_REJECTED: { step: TIMELINE_STEP.REJECTED, title: 'Rejected by a checker' },
    ALLOCATION_SHORTFALL: { step: TIMELINE_STEP.REJECTED, title: 'Rejected: too few units at approval' },
    HOLD_REJECTED: { step: TIMELINE_STEP.REJECTED, title: 'Rejected from hold' },
    T5: { step: TIMELINE_STEP.RECONCILE, title: 'Reconciled: money matched and cleared' },
    T6: { step: TIMELINE_STEP.RECONCILE, title: 'Reconciled: money matched, cheque awaiting clearance' },
    T7: { step: TIMELINE_STEP.RECONCILE, title: 'Reconciled: cheque cleared' },
    PART_RECEIVED: { step: TIMELINE_STEP.RECONCILE, title: 'Reconciled: part of the money received' },
    T12: { step: TIMELINE_STEP.APPROVE, title: 'Approved' },
    T13: { step: TIMELINE_STEP.UNITS, title: null }, // "Units created" or "Units released", by the order's type
    // IP-193: the payout (part) paid by the bank no longer matches the transaction's net, or it was paid for a transaction no
    // longer accepted - recorded by cash/payout-expect.js, left for a person.
    PAYOUT_CONFLICT: { step: TIMELINE_STEP.PAYOUT_ALERT, title: 'Payout needs attention: the bank\'s payment no longer matches this transaction' }
});

/** IP-193: what a PAYOUT_CONFLICT row says - the paid amount against the payout and the transaction's net now. */
function payoutAlertLine(h) {
    const d = h.details ?? {};
    const money = (v) => (v === null || v === undefined ? '—' : formatMoney(v) + ' LKR');
    return (h.reasonCode === 'PAID_BUT_NOT_ACCEPTED' ? 'Paid for a transaction no longer accepted: ' : 'Net now ' + money(d.net) + '; ')
        + 'payout ' + money(d.payoutAmount) + ', paid ' + money(d.paid);
}

/** A figure as the history shows it: units to 4 places, money to 2, a dash for none. */
const figureWords = (key, value) => (value === null || value === undefined ? '—' : key === 'units' ? formatUnits(value) : formatMoney(value));

/** The order types whose allocation adds units to the holder; every other type's takes them away. */
const UNITS_IN_TYPES = new Set(['SUB', 'SWI_IN', 'TRF_IN']);

/**
 * The transaction history (IP-187): the seven kinds of entry above, oldest first, from the
 * order's state history, its reviews and its pricing runs.
 *
 * @param {{ order: object, history?: object[], reviews?: object[], repricings?: object[] }} detail
 * @returns {Array<{ key: string, at: string, step: string, title: string, lines: string[], tone: 'blue'|'green'|'orange'|'red'|'gray' }>}
 */
export function transactionTimeline(detail) {
    const order = detail?.order ?? {};
    const entries = [];

    // The register's own moves: only those the owner listed (SHOWN_HISTORY).
    for (const h of detail?.history ?? []) {
        const shown = SHOWN_HISTORY[h.event];
        if (!shown) continue;
        const who = actorText(h.actor, h.actorName ?? null);
        if (shown.step === TIMELINE_STEP.UNITS) {
            // T13: the units the allocation posted, as the order now holds them.
            const created = UNITS_IN_TYPES.has(order.orderType);
            const units = order.allocatedUnits ?? order.units ?? null;
            entries.push({
                key: 'h' + h.historyId, at: h.occurredAt, step: shown.step, tone: 'green',
                title: created ? 'Units created' : 'Units released',
                lines: [units ? formatUnits(units) + ' units' + (order.struckPrice ? ' at ' + formatUnits(order.struckPrice) : '') : null, who]
                    .filter(Boolean)
            });
            continue;
        }
        entries.push({
            key: 'h' + h.historyId, at: h.occurredAt, step: shown.step, title: shown.title,
            tone: shown.step === TIMELINE_STEP.REJECTED || shown.step === TIMELINE_STEP.PAYOUT_ALERT ? 'red' : shown.step === TIMELINE_STEP.APPROVE ? 'green' : 'blue',
            // A rejection says why; a payout alert what was paid against what is owed; every entry says who.
            lines: [
                shown.step === TIMELINE_STEP.REJECTED ? (h.details?.reason ?? h.reasonCode ?? null) : null,
                shown.step === TIMELINE_STEP.PAYOUT_ALERT ? payoutAlertLine(h) : null,
                who
            ].filter(Boolean)
        });
    }

    // Reviews: each review with its comment, each send-back with its reason (IP-127).
    for (const r of detail?.reviews ?? []) {
        const review = reviewEntry(r);
        entries.push({
            key: 'r' + r.reviewId, at: r.occurredAt, step: TIMELINE_STEP.REVIEW, tone: review.sentBack ? 'orange' : 'green',
            title: (review.sentBack ? 'Sent back by ' : 'Reviewed by ') + review.who,
            lines: review.text ? [(review.sentBack ? 'Reason: ' : '') + review.text] : []
        });
    }

    // Pricing: each run that moved a figure, at its price, with what it moved.
    for (const p of detail?.repricings ?? []) {
        entries.push({
            key: 'p' + p.repriceSeq, at: p.occurredAt, step: TIMELINE_STEP.PRICING, tone: p.priceBasis === 'DEALING_DATE' ? 'green' : 'gray',
            title: p.priceApplied
                ? 'Priced at ' + formatUnits(p.priceApplied) + (p.priceBasis === 'DEALING_DATE' ? ' (dealing-date price)' : ' (provisional)')
                : 'Priced',
            lines: repricingChanges(p).map((c) => c.label + ': ' + figureWords(c.key, c.before) + ' → ' + figureWords(c.key, c.after))
                .concat(p.capped ? ['Capped at the units available'] : [])
                // A redemption paid to several bank accounts: what each is paid at this price.
                .concat(payoutSplitLines(p.payoutSplits, detail?.payoutAccounts ?? []))
        });
    }

    // Oldest first; entries at the same instant keep the order of the steps above.
    const rank = Object.values(TIMELINE_STEP);
    return entries.sort((a, b) => (Date.parse(a.at) - Date.parse(b.at)) || (rank.indexOf(a.step) - rank.indexOf(b.step)));
}

/**
 * A redemption's payout accounts as the drawer and the review show them
 * (orders/payout-split.js on the server): each account - bank, branch, name,
 * masked number, its split % - with its share of the units and of the LKR
 * paid. Until the Core prices the transaction only the side the investor
 * fixed is shown (figureOf's rule, US-05): an LKR redemption's LKR split, a
 * unit based one's units split; once priced, both, the LKR being the net.
 *
 * @param {{ order: object, payoutAccounts?: object[] }} detail GET orders/{id}
 * @returns {{ rows: Array<{ key: string, seq: number, bank: string, branch: string, accountName: string, account: string,
 *   splitPercent: string, units: string|null, amount: string|null, registered: boolean }>, amountLabel: string, totals: { units: string|null, amount: string|null } }}
 */
export function payoutRows(detail) {
    const order = detail?.order ?? {};
    const accounts = [...(detail?.payoutAccounts ?? [])].sort((a, b) => a.seq - b.seq);
    const hidden = awaitingRepricing(order) ? hiddenUntilRepriced(order) : [];
    const rows = accounts.map((a) => ({
        key: 'payout-' + a.seq,
        seq: a.seq,
        bank: a.bankName ?? '',
        branch: a.branch ?? '',
        accountName: a.accountName ?? '',
        account: a.accountMasked ?? '',
        splitPercent: a.splitPercent === null || a.splitPercent === undefined ? '' : String(a.splitPercent),
        units: hidden.includes('units') ? null : a.units ?? null,
        amount: hidden.includes('amount') ? null : a.amount ?? null,
        registered: a.registered !== false
    }));
    const basis = accounts.find((a) => a.amountBasis)?.amountBasis ?? null;
    const total = (key, dp) => (rows.length && rows.every((r) => r[key] !== null)
        ? rows.reduce((acc, r) => acc + Math.round(Number(r[key]) * 10 ** dp), 0) / 10 ** dp : null);
    return {
        rows,
        amountLabel: basis === 'NET' ? 'Paid (LKR, net)' : basis === 'REQUESTED' ? 'Requested (LKR)' : 'Amount (LKR)',
        totals: { units: total('units', 4), amount: total('amount', 2) }
    };
}

/** The lines a pricing run adds for each payout account: "Account 1 ••1234 (30%): 9,900.00 LKR". */
export function payoutSplitLines(splits, accounts = []) {
    if (!splits?.length) return [];
    const bySeq = new Map(accounts.map((a) => [a.seq, a]));
    return splits.map((s) => {
        const account = bySeq.get(s.seq);
        const who = 'Account ' + s.seq + (account?.accountMasked ? ' ' + account.accountMasked : '');
        return who + ' (' + Number(s.splitPercent) + '%): ' + (s.amount !== null && s.amount !== undefined ? formatMoney(s.amount) + ' LKR' : '—')
            + (s.units !== null && s.units !== undefined ? ' · ' + formatUnits(s.units) + ' units' : '');
    });
}

/** The figures a repricing moves, in the order the drawer lists them. */
const FIGURES = Object.freeze([['units', 'Units'], ['amount', 'Amount'], ['fee', 'Fee'], ['net', 'Net']]);

/**
 * One order_repricing row (design 8.4): the figures that changed, before and
 * after. A figure that did not move is left out.
 *
 * @returns {Array<{ key: string, label: string, before: string|null, after: string|null }>}
 */
export function repricingChanges(r) {
    const before = r?.before ?? {};
    const after = r?.after ?? {};
    return FIGURES
        .filter(([key]) => (before[key] ?? null) !== (after[key] ?? null))
        .map(([key, label]) => ({ key, label, before: before[key] ?? null, after: after[key] ?? null }));
}

/**
 * The review dialog's banner (design 8.3): the earliest cut-off ahead among
 * the orders' OPEN books - "Review before the cut-off (14:00). An order not
 * reviewed by then rolls to the next dealing day." - or, when every open
 * book's cut-off has passed, that this review counts for the next dealing
 * day. Null when no order is in an open book (frozen and later orders are
 * past their cut-off already).
 *
 * @param {object[]} orders
 * @param {number} nowMs
 * @returns {string|null}
 */
export function cutoffBanner(orders, nowMs = Date.now()) {
    const cutoffs = orders
        .filter((o) => o.book?.state === 'OPEN' && Number.isFinite(msOf(o.book.cutoffAt)))
        .map((o) => o.book.cutoffAt)
        .sort((a, b) => msOf(a) - msOf(b));
    if (!cutoffs.length) return null;
    const ahead = cutoffs.find((iso) => msOf(iso) > nowMs);
    if (ahead) return 'Review before the cut-off (' + colomboTime(ahead, nowMs) + '). A transaction not reviewed by then rolls to the next dealing day.';
    return 'The cut-off (' + colomboTime(cutoffs[cutoffs.length - 1], nowMs) + ') has passed: this review counts for the next dealing day, which the transaction rolls to when its book closes.';
}
